<template>
  <div v-if="societe && agence">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">{{ isTitleAddOrEdit }}</h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button is-cancel btn-modal-annuler" @click="$router.go(-1)">
              {{ $t('general.button.cancel') }}
            </button>
            <button class="button is-addOrEdit" @click="saveAgence()">
              {{ $t('general.button.sauvegarder') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <error-msg-component :messages="messages"
                          :error="error"
                          :title="'editionClient.problemOccurred'"
                          :withEmplyFields="true"
                          @close="error = $event"/>
    <div class="tab">
      <ul>
        <li :class="{'is-active': activeTab === 'informationsAgence'}"
            @click="activeTab = 'informationsAgence'">
          {{ $t('addAgence.tab.agence') }}
        </li>
        <li :class="{'is-active': activeTab === 'editionAgence'}"
            @click="activeTab = 'editionAgence'">
          {{ $t('addAgence.tab.edition') }}
        </li>
      </ul>
    </div>
    <component :agence="agence" :societe="societe" :is="activeTab">
    </component>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import AppBreadcrumb from '@/components/general/breadcrumb';
import messageErreur from '@/services/general/messageErreur';
import genererAgence from '@/services/societe/genererAgence';
import { get as getSociete, update as updateSociete } from '@/api/societe/index';
import validation from '@/services/parametrage/validation/validationSociete';
import messageSuccess from '@/services/general/messageSucces';
import * as types from '@/store/modules/societe-mutation-type';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import informationsAgence from '@/components/parametrage/agence/editionAgence/informationsAgence';
import editionAgence from '@/components/parametrage/agence/editionAgence/editionAgence';

const societe = createNamespacedHelpers('societe');

export default {
  data() {
    return {
      agence: genererAgence(),
      activeTab: 'informationsAgence',
      societe: null,
      messages: [],
      error: false,
    };
  },
  components: {
    AppBreadcrumb,
    errorMsgComponent,
    informationsAgence,
    editionAgence,
  },
  beforeRouteLeave(to, from, next) {
    if (!['m.parametrage.societe/agence'].includes(to.name)) {
      this.setSocieteCourante(null);
    }
    next();
  },
  computed: {
    ...societe.mapGetters({
      societeCourante: 'societeCourante',
    }),
    isTitleAddOrEdit() {
      if (this.$route.params.agenceId || this.$route.params.indexAgence !== undefined) {
        return this.$i18n.t('addAgence.titleModification');
      }
      return this.$i18n.t('addAgence.title');
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('listeUtilisateur.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('listeUtilisateur.parametrage'), route: { name: 'l.parametrage.general' }, isActive: false },
        { title: this.$i18n.t('societes.liste'), route: { name: 'l.parametrage.societe/agence' }, isActive: false },
        { title: this.isTitleAddOrEdit, route: { name: '' }, isActive: true },
      ];
    },
  },
  methods: {
    ...societe.mapActions({
      loadSocietes: 'loadSocietes',
    }),
    ...societe.mapMutations({
      setSocieteCourante: types.SET_SOCIETE_COURANTE,
    }),
    async sauvegardeReel() {
      const indexAgence = this.societe.agences.findIndex((agence) => {
        return agence.id === this.$route.params.agenceId;
      });
      this.societe.agences[indexAgence] = this.agence;
      await updateSociete(this.societe);
      await this.loadSocietes({});
      this.$router.go(-1);
      messageSuccess(this.$i18n.t('addAgence.success.update'));
    },
    async saveAgence() {
      const loadingComponent = this.$buefy.loading.open();
      this.messages = [];
      this.error = false;
      try {
        if (this.$route.params.agenceId) {
          await this.sauvegardeReel();
          return loadingComponent.close();
        }
        if (this.agence.libelle === null) {
          this.error = true;
          this.messages.push(this.$i18n.t('addAgence.error.libelle'));
          return loadingComponent.close();
        }
        if (this.$route.params.indexAgence !== undefined) {
          this.societe.agences[this.$route.params.indexAgence] = await cloneDeep(this.agence);
          messageSuccess(this.$i18n.t('addAgence.success.update'));
        } else {
          this.societe.agences.push(this.agence);
          messageSuccess(this.$i18n.t('addAgence.success.add'));
        }
        this.setSocieteCourante(this.societe);
        await this.loadSocietes({});
        this.$router.go(-1);
        return loadingComponent.close();
      } catch (e) {
        if (e.response && e.response.data.error) {
          validation(e.response, this.messages);
          this.error = true;
          return loadingComponent.close();
        }
        messageErreur(this.$i18n);
        loadingComponent.close();
        throw e;
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      if (this.societeCourante) {
        this.societe = await cloneDeep(this.societeCourante);
      } else if (!this.societeCourante && this.$route.params.societeId) {
        this.societe = (await getSociete(this.$route.params.societeId)).data.result;
      }
      if (!this.societe) {
        messageErreur(this.$i18n);
        await this.$router.push({ name: 'l.parametrage.societe/agence' });
      }
      if (this.$route.params.agenceId) {
        this.agence = this.societe.agences.find((agence) => {
          return agence.id === this.$route.params.agenceId;
        });
      }
      if (this.$route.params.indexAgence !== undefined) {
        this.agence = cloneDeep(this.societe.agences[this.$route.params.indexAgence]);
      }
      if (this.$route.params.agenceId || this.$route.params.indexAgence !== undefined) {
        this.setInformationPage(this.$t('pageGeneral.detail', { item: this.agence.libelle }));
      } else {
        this.setInformationPage(this.$t('pageParametrage.agence.creation', { item: this.societe.libelle }));
      }
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>

<style scoped>

</style>
